import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Loader2, MapPin, Home as HomeIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import UploadField from './UploadField';
import { useTheme } from '../ThemeProvider';

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';
const Card = ({ children }) => <div className="glass-card p-5">{children}</div>;
const Loader = () => <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

// =================== HOMEPAGE (Settings) ===================
export function HomeBackendManager() {
  const { refresh } = useTheme();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.getSettings().then(setData).catch(() => setData({})); }, []);
  if (!data) return <Loader />;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Includo solo le chiavi gestite da questa schermata, evitando di sovrascrivere altri settings
      const payload = {};
      const keys = ['edition', 'title', 'subtitle', 'date_range', 'year', 'logo_url',
        'hero_video_url', 'hero_image_url', 'presentation_video_url', 'patrocin_image_url',
        'patrocin_title', 'brochure_pdf_url'];
      keys.forEach((k) => { payload[k] = data[k] || ''; });
      await api.updateSettings(payload);
      toast.success('Homepage salvata');
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Errore di salvataggio');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="home-backend-manager">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-lg flex items-center gap-2"><HomeIcon size={18} className="text-pink-brand" /> Informazioni Edizione Attuale</h2>
          <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5" data-testid="home-save-button">
            <Save size={16} /> {saving ? 'Salvo…' : 'Salva tutto'}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className={lbl}>Edizione</label><input className={inp} value={data.edition || ''} onChange={(e) => set('edition', e.target.value)} placeholder="XIV Memorial" /></div>
          <div><label className={lbl}>Titolo</label><input className={inp} value={data.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Eugenio Abbro" /></div>
          <div><label className={lbl}>Sottotitolo</label><input className={inp} value={data.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} placeholder="di Calcio Giovanile" /></div>
          <div><label className={lbl}>Date</label><input className={inp} value={data.date_range || ''} onChange={(e) => set('date_range', e.target.value)} placeholder="25 / 31 Maggio 2026" /></div>
          <div><label className={lbl}>Anno</label><input className={inp} value={data.year || ''} onChange={(e) => set('year', e.target.value)} placeholder="2026" /></div>
        </div>
      </Card>

      <Card>
        <h2 className="text-white font-bold text-lg mb-4">Logo</h2>
        <UploadField label="Logo principale (PNG/SVG con sfondo trasparente)" accept="image/png,image/jpeg,image/svg+xml" folder="logo" value={data.logo_url} onChange={(v) => set('logo_url', v)} />
      </Card>

      <Card>
        <h2 className="text-white font-bold text-lg mb-4">Media Hero (homepage)</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <UploadField label="Video sfondo home" accept="video/mp4" folder="hero" value={data.hero_video_url} onChange={(v) => set('hero_video_url', v)} />
          <UploadField label="Immagine fallback (poster)" accept="image/*" folder="hero" value={data.hero_image_url} onChange={(v) => set('hero_image_url', v)} />
          <UploadField label="Video di presentazione (sezione dedicata)" accept="video/mp4" folder="presentazione" value={data.presentation_video_url} onChange={(v) => set('presentation_video_url', v)} />
          <UploadField label="Brochure PDF" accept="application/pdf" folder="brochure" value={data.brochure_pdf_url} onChange={(v) => set('brochure_pdf_url', v)} />
        </div>
      </Card>

      <Card>
        <h2 className="text-white font-bold text-lg mb-4">Patrocini</h2>
        <div className="space-y-4">
          <div><label className={lbl}>Titolo (testo sopra l'immagine)</label><input className={inp} value={data.patrocin_title || ''} onChange={(e) => set('patrocin_title', e.target.value)} /></div>
          <UploadField label="Immagine unica con tutti i patrocini (PNG trasparente)" accept="image/png,image/svg+xml" folder="patrocini" value={data.patrocin_image_url} onChange={(v) => set('patrocin_image_url', v)} />
        </div>
      </Card>

      <button onClick={save} disabled={saving} className="btn-primary" data-testid="home-save-button-bottom">
        <Save size={16} /> {saving ? 'Salvo…' : 'Salva tutto'}
      </button>
    </div>
  );
}

// =================== CAMPI ===================
export function CampiBackendManager() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.getCampi().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <Loader />;
  const reload = () => api.getCampi().then(setItems);

  const add = async () => {
    try { await api.saveCampo({ nome: 'Nuovo campo', indirizzo: '', tipo: 'Campo gare', sort_order: items.length }); reload(); }
    catch (e) { toast.error(e?.message); }
  };

  return (
    <div className="space-y-3" data-testid="campi-backend-manager">
      <button onClick={add} className="btn-primary !py-2 !px-4 !text-sm"><Plus size={14} /> Nuovo campo</button>
      {items.map((c) => <CampoRow key={c.id} item={c} onSaved={reload} />)}
      {items.length === 0 && <Card><p className="text-white/60 text-sm text-center py-4">Nessun campo. Clicca "Nuovo campo".</p></Card>}
    </div>
  );
}
function CampoRow({ item, onSaved }) {
  const [d, setD] = useState({ ...item });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const save = async () => { try { await api.saveCampo(d); toast.success('Campo salvato'); onSaved(); } catch (e) { toast.error(e?.message); } };
  const del = async () => { if (!window.confirm('Eliminare questo campo?')) return; try { await api.deleteCampo(d.id); onSaved(); } catch (e) { toast.error(e?.message); } };
  return (
    <Card>
      <div className="flex items-center gap-3 mb-3">
        <MapPin size={18} className="text-pink-brand" />
        <input className={inp + ' flex-1'} value={d.nome || ''} onChange={(e) => set('nome', e.target.value)} placeholder="Nome del campo" />
      </div>
      <div className="grid sm:grid-cols-12 gap-3">
        <div className="sm:col-span-7"><label className={lbl}>Indirizzo</label><input className={inp} value={d.indirizzo || ''} onChange={(e) => set('indirizzo', e.target.value)} /></div>
        <div className="sm:col-span-3"><label className={lbl}>Tipo</label><input className={inp} value={d.tipo || ''} onChange={(e) => set('tipo', e.target.value)} placeholder="Campo gare / Finali" /></div>
        <div className="sm:col-span-2"><label className={lbl}>Ordine</label><input type="number" className={inp} value={d.sort_order || 0} onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)} /></div>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={save} className="btn-primary !py-2 !px-4 !text-xs"><Save size={12} /> Salva</button>
        <button onClick={del} className="px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold uppercase">Elimina</button>
      </div>
    </Card>
  );
}
